import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, Pencil } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, get, post } from '../../nucleo/api';
import { data, dataHora, hojeIso, nomeDeArquivo, nomePessoa, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { campoEmpresaDoRegistro, empresaEscolhida, precisaEscolherEmpresa } from '../../nucleo/empresa/empresa-do-registro';
import { CampoEmpresaDaTela, useEmpresaDaTela } from '../../nucleo/empresa/EmpresaDaTela';
import { invalidarPonto, type ArquivoFiscal, type ConfigRepP } from './tipos';

/**
 * Lote 4: Arquivos fiscais. O ponto oficial (REP-P) da empresa do seletor (desligado por padrão) e os arquivos AFD e AEJ gerados
 * por período, com a assinatura destacada (.p7s) quando há certificado digital A1.
 */
export function Fiscal() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const [gerando, setGerando] = useState(false);
  const colunas: Coluna<ArquivoFiscal>[] = [
    { chave: 'tipo', rotulo: t('fiscal.campos.tipo'), principal: true, render: (a) => <span className="font-medium">{t(`fiscal.tipo.${a.tipo}`)}</span> },
    { chave: 'de', rotulo: t('fiscal.campos.periodo'), render: (a) => `${data(a.de)} - ${data(a.ate)}` },
    { chave: 'empresa_nome', rotulo: t('fiscal.campos.empresa'), render: (a) => a.empresa_nome ?? '-' },
    { chave: 'registros', rotulo: t('fiscal.campos.registros'), alinhar: 'direita', mono: true, render: (a) => numero(a.registros) },
    { chave: 'oficial', rotulo: t('fiscal.campos.situacao'), render: (a) => <Selo cor={a.oficial ? 'verde' : 'neutro'}>{a.oficial ? t('fiscal.oficial') : t('fiscal.naoOficial')}</Selo> },
    {
      chave: 'anexo_id',
      rotulo: t('fiscal.baixar'),
      render: (a) => (
        <span className="flex flex-wrap gap-3">
          <a className="text-acento-forte hover:underline" download href={`/api/v1/anexos/${a.anexo_id}`} title={nomeDeArquivo(a.nome_arquivo)}>
            {t('fiscal.baixar')}
          </a>
          {a.assinatura_anexo_id && (
            <a className="text-acento-forte hover:underline" download href={`/api/v1/anexos/${a.assinatura_anexo_id}`}>
              {t('fiscal.baixarAssinatura')}
            </a>
          )}
        </span>
      ),
    },
    { chave: 'created_at', rotulo: t('fiscal.campos.geradoEm'), oculta: true, render: (a) => `${dataHora(a.created_at)} · ${nomePessoa(a.gerado_por_nome) || '-'}` },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {s.pode('ponto.fechar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<FileText className="size-4" />} onClick={() => setGerando(true)}>
            {t('fiscal.gerar')}
          </Botao>
        </AcaoPrimaria>
      )}
      <RepP />
      <Tabela<ArquivoFiscal> tela="ponto.fiscal" caminho="/ponto/fiscal/arquivos" chaveConsulta={['ponto', 'fiscal']} colunas={colunas} vazio={<Vazio texto={t('fiscal.vazio')} />} />
      {gerando && <GerarArquivo aoFechar={() => setGerando(false)} />}
    </div>
  );
}

function RepP() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  // Rodada rval1-1 (RVAL1-ROB-M2): com a troca de empresa desligada não há seletor; a empresa do REP-P é escolhida aqui.
  const daTela = useEmpresaDaTela();
  const empresa = daTela.empresa;
  const [editando, setEditando] = useState(false);
  const q = useQuery({ queryKey: ['ponto', 'rep-p', empresa], queryFn: () => get<ConfigRepP>('/ponto/rep-p', empresa ? { empresa_id: empresa } : {}), enabled: !!empresa });
  if (!empresa) return <p className="text-sm text-tinta-3">{t('fiscal.semEmpresa')}</p>;
  return (
    <>
      <CampoEmpresaDaTela id="ponto-rep-p-empresa" estado={daTela} />
      <Estado consulta={q}>
        {(c) => (
          <Cartao
            titulo={t('fiscal.repP')}
            acao={
              s.pode('ponto.configurar') ? (
                <button type="button" aria-label={t('fiscal.editar')} onClick={() => setEditando(true)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                  <Pencil className="size-4" />
                </button>
              ) : undefined
            }
          >
            <p className="mb-3 text-sm text-tinta-3">{t('fiscal.avisoRepP')}</p>
            <dl className="grid gap-x-6 text-sm sm:grid-cols-2">
              {(
                [
                  ['ativo', c.ativo ? t('fiscal.ligado') : t('fiscal.desligado')],
                  ['situacao', c.oficial ? t('fiscal.oficial') : t('fiscal.naoOficial')],
                  ['inpi', c.inpi ?? '-'],
                  ['local', c.local_prestacao ?? '-'],
                  ['certificado', c.certificado_configurado ? t('fiscal.certificado.sim') : t('fiscal.certificado.nao')],
                  ['nsr', String(c.ultimo_nsr)],
                  ['desenvolvedorRazao', c.desenvolvedor_razao ?? '-'],
                  ['desenvolvedorDocumento', c.desenvolvedor_documento ?? '-'],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
                  <dt className="text-tinta-3">{t(`fiscal.campos.${k}`)}</dt>
                  <dd className="min-w-0 break-words text-right text-tinta-1">{v}</dd>
                </div>
              ))}
            </dl>
          </Cartao>
        )}
      </Estado>
      {editando && q.data && <EditarRepP atual={q.data} aoFechar={() => setEditando(false)} />}
    </>
  );
}

/**
 * RCRM5 SPEC-B4 (E3, "CNPJ preenche os campos seguintes vazios em todo formulário"): o CNPJ do desenvolvedor consulta a Receita
 * pela mesma rota do Cadastros (`/cadastros/consultas/cnpj`, que exige `cadastros.ver`) e leva razão social e e-mail aos vazios.
 * Sem o Cadastros ou sem a permissão, não consulta (o preenchimento segue à mão). Testada em `tests/rcrm5-fix-ponto.test.ts`.
 */
export async function consultarDesenvolvedor(cnpj: string): Promise<Record<string, unknown> | null> {
  try {
    const r = await get<{ situacao: string | null; dados: Record<string, unknown> | null }>(`/cadastros/consultas/cnpj/${encodeURIComponent(cnpj)}`);
    if (r.situacao !== 'encontrado' || !r.dados) return null;
    const txt = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
    return { desenvolvedor_razao: txt(r.dados.razao_social) ?? txt(r.dados.nome_fantasia), desenvolvedor_email: txt(r.dados.email) };
  } catch {
    return null;
  }
}

export function EditarRepP({ atual, aoFechar }: { atual: ConfigRepP; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const s = useSessao();
  const podeConsultar = s.instalado('cadastros') && s.pode('cadastros.ver');
  const campos: DefCampo[] = [
    { nome: 'ativo', rotulo: t('fiscal.campos.ativo'), tipo: 'booleano', ajuda: t('fiscal.avisoLigar') },
    { nome: 'inpi', rotulo: t('fiscal.campos.inpi'), tipo: 'texto', ajuda: t('fiscal.campos.inpiAjuda'), max: 30 },
    { nome: 'local_prestacao', rotulo: t('fiscal.campos.local'), tipo: 'texto', max: 100 },
    { nome: 'caepf_cno', rotulo: t('fiscal.campos.caepfCno'), tipo: 'texto', ajuda: t('fiscal.campos.caepfCnoAjuda'), max: 30 },
    { nome: 'desenvolvedor_documento', rotulo: t('fiscal.campos.desenvolvedorDocumento'), tipo: 'documento', documento: 'qualquer', ajuda: t('fiscal.campos.desenvolvedorAjuda'), ...(podeConsultar ? { preencher: consultarDesenvolvedor } : {}) },
    { nome: 'desenvolvedor_razao', rotulo: t('fiscal.campos.desenvolvedorRazao'), tipo: 'texto', max: 150 },
    { nome: 'desenvolvedor_email', rotulo: t('fiscal.campos.desenvolvedorEmail'), tipo: 'email', max: 50 },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('fiscal.editar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-rep-p">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-rep-p"
        campos={campos}
        inicial={{ ...atual, inpi: atual.inpi ?? '', local_prestacao: atual.local_prestacao ?? '', caepf_cno: atual.caepf_cno ?? '', desenvolvedor_documento: atual.desenvolvedor_documento ?? '', desenvolvedor_razao: atual.desenvolvedor_razao ?? '', desenvolvedor_email: atual.desenvolvedor_email ?? '' }}
        aoEnviar={async (v) => {
          await api('PUT', '/ponto/rep-p', {
            empresa_id: atual.empresa_id,
            ativo: !!v.ativo,
            inpi: v.inpi || null,
            local_prestacao: v.local_prestacao || null,
            caepf_cno: v.caepf_cno || null,
            desenvolvedor_documento: v.desenvolvedor_documento || null,
            desenvolvedor_razao: v.desenvolvedor_razao || null,
            desenvolvedor_email: v.desenvolvedor_email || null,
          });
          await invalidarPonto(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Drawer>
  );
}

function GerarArquivo({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => crypto.randomUUID(), []);
  const empresa = s.empresas?.atual ?? null;
  // Rodada rval1-1 (RVAL1-ROB-M2): sem empresa atual e com mais de uma, a empresa do arquivo vem do formulário.
  const escolherEmpresa = precisaEscolherEmpresa(s.empresas);
  const hoje = hojeIso();
  const inicioMes = `${hoje.slice(0, 7)}-01`;
  const campos: DefCampo[] = [
    ...campoEmpresaDoRegistro(s.empresas, { rotulo: t('fiscal.campos.empresa'), ajuda: t('fiscal.semEmpresa') }),
    { nome: 'tipo', rotulo: t('fiscal.campos.tipo'), tipo: 'selecao', obrigatorio: true, opcoes: (['aej', 'afd'] as const).map((x) => ({ valor: x, rotulo: t(`fiscal.tipo.${x}`) })) },
    { nome: 'de', rotulo: t('fiscal.campos.de'), tipo: 'data', obrigatorio: true, largura: 'meia' },
    { nome: 'ate', rotulo: t('fiscal.campos.ate'), tipo: 'data', obrigatorio: true, largura: 'meia' },
  ];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('fiscal.gerarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-arquivo-fiscal" aria-disabled={!empresa && !escolherEmpresa}>
            {t('fiscal.gerar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{empresa || escolherEmpresa ? t('fiscal.avisoGerar') : t('fiscal.semEmpresa')}</p>
      <Formulario
        id="form-arquivo-fiscal"
        campos={campos}
        inicial={{ tipo: 'aej', de: inicioMes, ate: hoje }}
        aoEnviar={async (v) => {
          const daEmpresa = empresa ?? empresaEscolhida(v);
          if (!daEmpresa) throw new Error(t('fiscal.semEmpresa'));
          await post('/ponto/fiscal/arquivos', { id, tipo: v.tipo, de: v.de, ate: v.ate, empresa_id: daEmpresa });
          await invalidarPonto(qc);
          toast.sucesso(t('fiscal.gerado'));
          aoFechar();
        }}
      />
    </Modal>
  );
}
